import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryState,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getPageLinkByNodeId,
    getPageLinkByPageType,
    LinkModel,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import { Clickable, GridItem } from "@insite/mobius/index";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowRightLongIcon from "@tCentro/Icons/ArrowRightLongIcon";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";
import { useCategoriesLinks } from "@tCentro/Store/CustomLinksSelectors";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import React, { useEffect } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const enum fields {
    divisionCategories = "divisionCategories",
    articlesType = "articlesType",
}

interface DivisionCategoryModel {
    title: string;
    url: string;
    smallImagePath?: string;
    categoryDescription?: string;
    categoryPath?: string;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.divisionCategories]: LinkModel[];
        [fields.articlesType]: string;
    };
}

const mapStateToProps = (state: ApplicationState & CustomApplicationState) => {
    const location = getLocation(state);
    const { pathname, search } = getLocation(state);

    return {
        location,
        pathname,
        search,
        cenSelectedArticleType: state.t_components.selectedArticleType.selectedArticleType,
        categoriesPageLink: getPageLinkByPageType(state, "CategoryListPage"),
        websiteName: state.context.website.name,
    };
};

type Props = OwnProps & WidgetProps & ReturnType<typeof mapStateToProps>;

export interface CENDivisionCategoryListStyles {
    container?: GridContainerProps;
    categoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    categoryImageItem?: GridItemProps;
    imageWrapper?: InjectableCss;
    categoryImage?: InjectableCss;
    categoryNameLinkItem?: GridItemProps;
    categoryName?: InjectableCss;
    descriptionStyles?: InjectableCss;
    hoveredLink?: InjectableCss;
    categoryWrapper?: InjectableCss;
    mainTitleStyles?: InjectableCss;
    mainContainer?: InjectableCss;
    rightArrowIconStyles?: IconPresentationProps;
    linkContainerStyles?: InjectableCss;
    linkStyles?: InjectableCss;
}

export const cenDivisionCategoryListStyles: CENDivisionCategoryListStyles = {
    linkStyles: {
        css: css`
            font-family: Poppins;
            font-weight: 600;
            font-size: 18px;
            line-height: 24px;
        `,
    },
    linkContainerStyles: {
        css: css`
            display: flex;
            gap: 4px;
            align-items: center;
            width: 100%;
            justify-content: center;
            margin-top: 40px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 0px;
                        margin-bottom: 40px;
                    `,
                    css`
                        margin-top: 0px;
                        margin-bottom: 40px;
                    `,
                    css`
                        margin-top: 0px;
                        margin-bottom: 28px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    rightArrowIconStyles: {
        css: css`
            height: 11.25px !important;
            width: 18px !important;
            svg {
                height: 11.25px !important;
                width: 18px !important;
            }
        `,
    },
    mainContainer: {
        css: css`
            margin-bottom: 64px;
        `,
    },
    mainTitleStyles: {
        css: css`
            margin-top: 64px;
            margin-bottom: 20px;
            text-align: center;
            font-family: Poppins;
            font-weight: 700;
            font-size: 32px;
            line-height: 40px;
            letter-spacing: 0%;
            text-align: center;
            color: ${getProp("theme.typography.p.color")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-family: Poppins;
                        font-weight: 600;
                        font-size: 24px;
                        line-height: 32px;
                        letter-spacing: 0%;
                        text-align: center;
                    `,
                    css`
                        font-family: Poppins;
                        font-weight: 600;
                        font-size: 24px;
                        line-height: 32px;
                        letter-spacing: 0%;
                        text-align: center;
                    `,
                    css`
                        margin-bottom: 4px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    categoryWrapper: {
        css: css`
            padding: 12px 32px 32px 32px;
        `,
    },
    descriptionStyles: {
        css: css`
            margin-bottom: 16px;
            margin-top: 10px;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            -webkit-line-clamp: 4;
            p {
                font-size: ${TYPOGRAPHY.size.smaller} !important;
                font-weight: ${TYPOGRAPHY.weight.base};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                font-family: "Poppins" !important;
                color: ${getProp("theme.typography.p.color")};
            }
            html body {
                background: transparent;
                font-size: ${TYPOGRAPHY.size.smaller} !important;
                font-weight: ${TYPOGRAPHY.weight.base};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                font-family: "Poppins" !important;
                color: ${getProp("theme.typography.p.color")};
                p {
                    font-size: ${TYPOGRAPHY.size.smaller} !important;
                    font-weight: ${TYPOGRAPHY.weight.base};
                    line-height: ${TYPOGRAPHY.lineHeight.base};
                    font-family: "Poppins" !important;
                    color: ${getProp("theme.typography.p.color")};
                }
            }
        `,
    },
    categoryItem: {
        width: [12, 6, 6, 4, 3],
        css: css`
            padding: 12px !important;
            margin-top: 20px !important;
            div[class^="GridWrapper"] {
                height: 100%;
                width: 100%;
                background: rgb(242, 242, 242);
                border: 1px solid rgb(242, 242, 242);
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 56px;
                    `,
                    css`
                        margin-bottom: 78px;
                    `,
                    null,
                    null,
                    null,
                ])}
            @media (min-width: 1200px) {
                flex-basis: 25%;
                max-width: 25%;
            }
        `,
    },
    innerContainer: {
        gap: 24,
        css: css`
            background: ${COLORS.white};
        `,
    },
    container: {
        css: css`
            > div[class^="GridOffset"] {
                justify-content: center;
            }
        `,
    },
    categoryImageItem: {
        width: 12,
        css: css`
            height: 100%;
        `,
    },
    imageWrapper: {
        css: css`
            a {
                justify-content: center;
                height: 100%;
            }
        `,
    },
    categoryImage: {
        css: css`
            width: 100%;
            background: ${COLORS.white};
            img {
                width: 100%;
                object-fit: contain;
                height: auto;
            }
        `,
    },
    categoryNameLinkItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: left;
            min-height: 48px;
        `,
    },
    categoryName: {
        css: css`
            span {
                color: ${COLORS.text.base} !important;
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                font-size: ${TYPOGRAPHY.size.large};
                line-height: 24px;
                vertical-align: middle;
                width: 100%;
                overflow-wrap: break-word;
            }
        `,
    },
    hoveredLink: {
        css: css`
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: ${TYPOGRAPHY.size.smaller};
            line-height: 16px;
            color: ${getColor("text.link")};
        `,
    },
};

const styles = cenDivisionCategoryListStyles;

const CENArticleCategoryTypeBlogList = ({
    fields,
    search,
    cenSelectedArticleType,
    location,
    categoriesPageLink,
    websiteName
}: Props) => {
    const links = useCategoriesLinks(fields.divisionCategories, o => o.fields.destination) as DivisionCategoryModel[];
    const isArticleType = search.includes(`${fields.articlesType}=true`);
    const isAllArticleCategoriesType =
        fields.articlesType === "allArticlesType" && cenSelectedArticleType === "allArticlesType";

    if (!isArticleType && !isAllArticleCategoriesType) {
        return null;
    }

    return (
        <StyledWrapper {...styles.mainContainer}>
            <Typography as="p" {...styles.mainTitleStyles}>
                {translate(`Shop ${websiteName} Products`)}
            </Typography>
            <Hidden above="md">
                <StyledWrapper {...styles.linkContainerStyles}>
                    <Link href={categoriesPageLink?.url} {...styles.linkStyles}>
                        {translate("Shop all categories")}
                    </Link>
                    <Icon src={ArrowRightLongIcon} {...styles.rightArrowIconStyles} />
                </StyledWrapper>
            </Hidden>
            <GridContainer {...styles.container}>
                {links.map((link, index) => {
                    return (
                        <GridItem key={index} {...styles.categoryItem}>
                            <GridContainer {...styles.innerContainer}>
                                <GridItem {...styles.categoryImageItem}>
                                    <Link href={link?.categoryPath}>
                                        <StyledWrapper
                                            css={css`
                                                display: flex;
                                                flex-direction: column;
                                                width: 100%;
                                                align-items: initial;
                                            `}
                                        >
                                            <StyledWrapper {...styles.imageWrapper}>
                                                <LazyImage
                                                    {...styles.categoryImage}
                                                    src={link?.smallImagePath}
                                                    altText={fields.divisionCategories[index].fields.overriddenTitle || link.title}
                                                ></LazyImage>
                                            </StyledWrapper>

                                            <StyledWrapper {...styles.categoryWrapper}>
                                                <StyledWrapper {...styles.categoryNameLinkItem}>
                                                    <Link
                                                        className="list-link"
                                                        href={link.url}
                                                        target={
                                                            fields.divisionCategories[index].fields.openInNewWindow
                                                                ? "_blank"
                                                                : ""
                                                        }
                                                        {...styles.categoryName}
                                                    >
                                                        {fields.divisionCategories[index].fields.overriddenTitle ||
                                                            link.title}
                                                    </Link>
                                                </StyledWrapper>

                                                <StyledWrapper {...styles.descriptionStyles}>
                                                    {link?.categoryDescription &&
                                                        parse(
                                                            transformContentHtml(link?.categoryDescription),
                                                            parserOptions,
                                                        )}
                                                </StyledWrapper>

                                                <Link href={link?.categoryPath} {...styles.hoveredLink}>
                                                    {translate("Shop Products")}
                                                </Link>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </Link>
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                    );
                })}
            </GridContainer>
            <Hidden below="lg">
                <StyledWrapper {...styles.linkContainerStyles}>
                    <Link href={categoriesPageLink?.url} {...styles.linkStyles}>
                        {translate("Shop all categories")}
                    </Link>
                    <Icon src={ArrowRightLongIcon} {...styles.rightArrowIconStyles} />
                </StyledWrapper>
            </Hidden>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CENArticleCategoryTypeBlogList),
    definition: {
        group: "Common",
        displayName: "Article  Category Type BlogList",
        fieldDefinitions: [
            {
                name: fields.articlesType,
                displayName: "Select Article Type",
                editorTemplate: "RadioButtonsField",
                defaultValue: "",
                horizontal: false,
                fieldType: "General",
                options: [
                    {
                        displayName: "All Categories",
                        value: "allArticlesType",
                    },
                    {
                        displayName: "Automation & Robotics",
                        value: "AutomationRobotics",
                    },
                    {
                        displayName: "Instrumentation, Measurement & Control",
                        value: "InstrumentationMeasurementControl",
                    },
                    {
                        displayName: "Bulk Material Handling & Dust Collection",
                        value: "BulkMaterialHandlingDustCollection",
                    },
                    {
                        displayName: "Filtration & Process Systems",
                        value: "FiltrationProcessSystems",
                    },
                    {
                        displayName: "Pump & Seal",
                        value: "PumpSeal",
                    },
                    {
                        displayName: "Valve, Actuation & Engineered Products",
                        value: "ValveActuationEngineeredProducts",
                    },
                    {
                        displayName: "General",
                        value: "General",
                    },
                ],
            },

            {
                name: fields.divisionCategories,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                isVisible: item => item.fields[fields.articlesType] !== "",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        allowUrls: () => false,
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
